<!-- views/HomeView.vue -->
<template>
  <div class="home">
    <!-- If logged in, show a simplified feed + courses -->
    <div v-if="isAuthenticated" class="logged-in-home">
      <div class="welcome-banner">
        <h1>Welcome back, {{ userDisplayName }}! 👋</h1>
        <p>Continue learning or share your knowledge with the community</p>
        <div class="quick-actions">
          <button class="btn-primary" @click="$router.push('/feed')">
            View My Feed
          </button>
          <button class="btn-secondary" @click="$router.push('/studio')">
            Create Course
          </button>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="quick-stats">
        <div class="stat-card">
          <span class="number">{{ user?.enrolledCourses?.length || 0 }}</span>
          <span class="label">Courses Enrolled</span>
        </div>
        <div class="stat-card">
          <span class="number">{{ user?.createdCourses?.length || 0 }}</span>
          <span class="label">Courses Created</span>
        </div>
        <div class="stat-card">
          <span class="number">{{ user?.followers?.length || 0 }}</span>
          <span class="label">Followers</span>
        </div>
      </div>
    </div>

    <!-- Regular home page content (courses) -->
    <div class="home-content">
      <!-- Hero Section -->
      <section class="hero-section">
        <div class="hero-content">
          <div class="hero-text">
            <h1>Learn. Create. <span>Showcase.</span></h1>
            <p>
              Build your professional portfolio through courses. Share your
              knowledge, grow your career.
            </p>
            <div class="hero-actions">
              <button class="btn-primary" @click="scrollToCourses">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                Explore Courses
              </button>
              <button class="btn-secondary" @click="handleCreateCourse">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Start Teaching
              </button>
            </div>
          </div>
          <div class="hero-stats">
            <div class="stat">
              <span class="stat-number">12K+</span>
              <span class="stat-label">Active Learners</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat">
              <span class="stat-number">500+</span>
              <span class="stat-label">Courses</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat">
              <span class="stat-number">200+</span>
              <span class="stat-label">Creators</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Categories Bar -->
      <section class="categories-bar">
        <div class="categories-scroll">
          <button
            v-for="category in categories"
            :key="category.id"
            class="category-chip"
            :class="{ active: activeCategory === category.id }"
            @click="filterByCategory(category.id)"
          >
            <span class="category-icon">{{ category.icon }}</span>
            {{ category.name }}
          </button>
        </div>
      </section>

      <!-- Main Content -->
      <section class="courses-section" ref="coursesSection">
        <div class="section-header">
          <div class="header-left">
            <h2>🔥 Trending Courses</h2>
            <span class="subtitle">What's popular right now</span>
          </div>
          <div class="header-right">
            <button class="view-all" @click="viewAllCourses">
              View All
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>

        <div class="courses-grid">
          <CourseCard
            v-for="course in trendingCourses"
            :key="course.id"
            :course="course"
            @click="navigateToCourse(course.id)"
          />
        </div>
      </section>

      <!-- Featured Creators -->
      <section class="creators-section">
        <div class="section-header">
          <h2>🌟 Featured Creators</h2>
        </div>
        <div class="creators-scroll">
          <div
            v-for="creator in featuredCreators"
            :key="creator.id"
            class="creator-card"
            @click="navigateToCreator(creator.id)"
          >
            <img :src="creator.avatar" :alt="creator.name" />
            <h4>{{ creator.name }}</h4>
            <p>{{ creator.courses }} courses</p>
            <span class="creator-badge">⭐ {{ creator.rating }}</span>
          </div>
        </div>
      </section>

      <!-- New Courses Section -->
      <section class="courses-section">
        <div class="section-header">
          <h2>🆕 New Releases</h2>
          <span class="subtitle">Fresh content added this week</span>
        </div>
        <div class="courses-grid">
          <CourseCard
            v-for="course in newCourses"
            :key="course.id"
            :course="course"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useCourseStore } from "@/stores/course";
import CourseCard from "@/components/courses/CourseCard.vue";

const router = useRouter();
const authStore = useAuthStore();
const courseStore = useCourseStore();

// Computed - properly access auth store
const isAuthenticated = computed(() => authStore.isAuthenticated);
const user = computed(() => authStore.user);
const userDisplayName = computed(() => {
  return (
    authStore.user?.displayName ||
    authStore.user?.email?.split("@")[0] ||
    "Student"
  );
});

// State
const coursesSection = ref(null);
const activeCategory = ref("all");
const loading = ref(false);

const categories = ref([
  { id: "all", name: "All", icon: "🎯" },
  { id: "programming", name: "Programming", icon: "💻" },
  { id: "design", name: "Design", icon: "🎨" },
  { id: "business", name: "Business", icon: "📊" },
  { id: "marketing", name: "Marketing", icon: "📈" },
  { id: "ai", name: "AI & ML", icon: "🤖" },
  { id: "languages", name: "Languages", icon: "🌍" },
  { id: "music", name: "Music", icon: "🎵" },
]);

// Mock data - Replace with API calls
const trendingCourses = ref([
  {
    id: 1,
    title: "Python for Data Science",
    creator: "Ahmed Ali",
    creatorAvatar:
      "https://ui-avatars.com/api/?name=Ahmed+Ali&background=FF8000&color=fff",
    thumbnail: "https://picsum.photos/seed/python/400/225",
    price: 49.99,
    rating: 4.8,
    students: 2340,
    category: "programming",
    isNew: false,
    isTrending: true,
  },
  {
    id: 2,
    title: "UI/UX Design Masterclass",
    creator: "Sarah Hassan",
    creatorAvatar:
      "https://ui-avatars.com/api/?name=Sarah+Hassan&background=FF8000&color=fff",
    thumbnail: "https://picsum.photos/seed/design/400/225",
    price: 39.99,
    rating: 4.9,
    students: 1850,
    category: "design",
    isNew: true,
    isTrending: true,
  },
  {
    id: 3,
    title: "Digital Marketing 101",
    creator: "Mohammed Jaber",
    creatorAvatar:
      "https://ui-avatars.com/api/?name=Mohammed+Jaber&background=FF8000&color=fff",
    thumbnail: "https://picsum.photos/seed/marketing/400/225",
    price: 29.99,
    rating: 4.6,
    students: 3200,
    category: "marketing",
    isNew: false,
    isTrending: true,
  },
  {
    id: 4,
    title: "Machine Learning with Python",
    creator: "Zainab Khalid",
    creatorAvatar:
      "https://ui-avatars.com/api/?name=Zainab+Khalid&background=FF8000&color=fff",
    thumbnail: "https://picsum.photos/seed/ml/400/225",
    price: 59.99,
    rating: 4.7,
    students: 1560,
    category: "ai",
    isNew: true,
    isTrending: true,
  },
]);

const newCourses = ref([
  // Similar structure as above - more courses
]);

const featuredCreators = ref([
  {
    id: 1,
    name: "Ahmed Ali",
    avatar:
      "https://ui-avatars.com/api/?name=Ahmed+Ali&background=FF8000&color=fff&size=80",
    courses: 12,
    rating: 4.9,
  },
  {
    id: 2,
    name: "Sarah Hassan",
    avatar:
      "https://ui-avatars.com/api/?name=Sarah+Hassan&background=FF8000&color=fff&size=80",
    courses: 8,
    rating: 4.8,
  },
  // Add more creators
]);

// Methods
const scrollToCourses = () => {
  coursesSection.value?.scrollIntoView({ behavior: "smooth" });
};

const handleCreateCourse = () => {
  if (!isAuthenticated.value) {
    // Redirect to login
    router.push("/?redirect=/studio");
    return;
  }
  router.push("/studio");
};

const filterByCategory = (categoryId) => {
  activeCategory.value = categoryId;
  // Fetch courses by category
};

const viewAllCourses = () => {
  router.push("/explore");
};

const navigateToCourse = (courseId) => {
  router.push(`/course/${courseId}`);
};

const navigateToCreator = (creatorId) => {
  router.push(`/creator/${creatorId}`);
};

// Fetch courses on mount
onMounted(async () => {
  // Load courses from API
  // await courseStore.fetchCourses();
});
</script>

<style lang="scss" scoped>
$primary-orange: #ff8000;
$primary-blue: #001829;
$hover-orange: #cc5800;

.home {
  padding-top: 72px;
  background: #f8f9fa;
  min-height: 100vh;
}

/* Logged In Banner */
.logged-in-home {
  background: linear-gradient(135deg, $primary-blue, #003355);
  padding: 32px 24px;
  padding-top: $navheight;
  margin-bottom: 0;

  .welcome-banner {
    max-width: 1200px;
    margin: 0 auto;

    h1 {
      font-size: 32px;
      color: #fff;
      margin: 0 0 8px 0;
    }

    p {
      color: rgba(255, 255, 255, 0.8);
      font-size: 18px;
      margin: 0 0 16px 0;
    }

    .quick-actions {
      display: flex;
      gap: 12px;

      .btn-primary {
        padding: 10px 24px;
        background: $primary-orange;
        color: #fff;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: $hover-orange;
          transform: translateY(-2px);
        }
      }

      .btn-secondary {
        padding: 10px 24px;
        background: rgba(255, 255, 255, 0.15);
        color: #fff;
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.25);
        }
      }
    }
  }

  .quick-stats {
    max-width: 1200px;
    margin: 16px auto $navheight;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 16px;

    .stat-card {
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);
      padding: 16px;
      border-radius: 12px;
      text-align: center;

      .number {
        display: block;
        font-size: 28px;
        font-weight: 700;
        color: #fff;
      }

      .label {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }
}

/* Hero Section */
.hero-section {
  background: linear-gradient(135deg, $primary-blue 0%, #003355 100%);
  padding: 60px 24px 80px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -20%;
    width: 600px;
    height: 600px;
    background: radial-gradient(
      circle,
      rgba(255, 128, 0, 0.1) 0%,
      transparent 70%
    );
    border-radius: 50%;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -30%;
    left: -10%;
    width: 400px;
    height: 400px;
    background: radial-gradient(
      circle,
      rgba(255, 128, 0, 0.08) 0%,
      transparent 70%
    );
    border-radius: 50%;
  }
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.hero-text {
  text-align: center;
  margin-bottom: 48px;

  h1 {
    font-size: 56px;
    font-weight: 800;
    color: #fff;
    margin-bottom: 16px;
    letter-spacing: -1px;

    span {
      background: linear-gradient(135deg, $primary-orange, #ffa500);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    @media (max-width: 768px) {
      font-size: 36px;
    }
  }

  p {
    font-size: 20px;
    color: rgba(255, 255, 255, 0.8);
    max-width: 600px;
    margin: 0 auto 32px;
    line-height: 1.6;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  .hero-actions {
    display: flex;
    gap: 16px;
    justify-content: center;
    flex-wrap: wrap;

    button {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 14px 28px;
      border: none;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;

      svg {
        width: 20px;
        height: 20px;
      }

      &:active {
        transform: scale(0.95);
      }
    }

    .btn-primary {
      background: $primary-orange;
      color: #fff;

      &:hover {
        background: $hover-orange;
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(255, 128, 0, 0.3);
      }
    }

    .btn-secondary {
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
      backdrop-filter: blur(10px);

      &:hover {
        background: rgba(255, 255, 255, 0.25);
        transform: translateY(-2px);
      }
    }
  }
}

.hero-stats {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 40px;

  .stat {
    text-align: center;

    .stat-number {
      display: block;
      font-size: 32px;
      font-weight: 800;
      color: #fff;
      letter-spacing: -0.5px;
    }

    .stat-label {
      font-size: 14px;
      color: rgba(255, 255, 255, 0.6);
      margin-top: 4px;
    }
  }

  .stat-divider {
    width: 1px;
    height: 40px;
    background: rgba(255, 255, 255, 0.15);
  }

  @media (max-width: 768px) {
    gap: 20px;

    .stat .stat-number {
      font-size: 24px;
    }

    .stat-divider {
      height: 30px;
    }
  }
}

/* Categories Bar */
.categories-bar {
  position: sticky;
  top: 72px;
  z-index: 100;
  background: #fff;
  padding: 16px 24px;
  border-bottom: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  .categories-scroll {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 4px 0;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    .category-chip {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 20px;
      border: none;
      border-radius: 20px;
      background: #f1f3f5;
      color: #495057;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      white-space: nowrap;
      transition: all 0.3s ease;

      .category-icon {
        font-size: 16px;
      }

      &:hover {
        background: #e9ecef;
        transform: translateY(-1px);
      }

      &.active {
        background: $primary-orange;
        color: #fff;
        box-shadow: 0 4px 12px rgba(255, 128, 0, 0.3);
      }
    }
  }
}

/* Courses Section */
.courses-section {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 24px;

    .header-left {
      h2 {
        font-size: 28px;
        font-weight: 700;
        color: #1a1a1a;
        margin: 0 0 4px 0;
      }

      .subtitle {
        font-size: 14px;
        color: #868e96;
      }
    }

    .view-all {
      display: flex;
      align-items: center;
      gap: 4px;
      background: none;
      border: none;
      color: $primary-orange;
      font-weight: 600;
      cursor: pointer;
      padding: 8px 12px;
      border-radius: 8px;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(255, 128, 0, 0.1);
      }
    }
  }
}

.courses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 16px;
  }
}

/* Creators Section */
.creators-section {
  background: #fff;
  padding: 40px 24px;
  border-top: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;

  .section-header {
    max-width: 1200px;
    margin: 0 auto 24px;

    h2 {
      font-size: 28px;
      font-weight: 700;
      color: #1a1a1a;
      margin: 0;
    }
  }
}

.creators-scroll {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 24px;
  overflow-x: auto;
  padding: 8px 0;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  .creator-card {
    flex: 0 0 160px;
    text-align: center;
    padding: 20px;
    border-radius: 16px;
    background: #f8f9fa;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    }

    img {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      margin-bottom: 12px;
      border: 3px solid $primary-orange;
    }

    h4 {
      font-size: 16px;
      font-weight: 600;
      color: #1a1a1a;
      margin: 0 0 4px 0;
    }

    p {
      font-size: 13px;
      color: #868e96;
      margin: 0 0 8px 0;
    }

    .creator-badge {
      display: inline-block;
      padding: 2px 12px;
      background: $primary-orange;
      color: #fff;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
    }
  }
}

/* Responsive */
@media (max-width: 768px) {
  .hero-section {
    padding: 40px 16px 60px;

    .hero-text h1 {
      font-size: 32px;
    }
  }

  .courses-grid {
    grid-template-columns: 1fr 1fr;
  }

  .creators-scroll .creator-card {
    flex: 0 0 140px;
  }

  .logged-in-home {
    .welcome-banner {
      h1 {
        font-size: 24px;
      }

      .quick-actions {
        flex-direction: column;

        button {
          width: 100%;
          justify-content: center;
        }
      }
    }

    .quick-stats {
      grid-template-columns: 1fr 1fr 1fr;
    }
  }
}

@media (max-width: 480px) {
  .courses-grid {
    grid-template-columns: 1fr;
  }

  .logged-in-home .quick-stats {
    grid-template-columns: 1fr;
  }
}
</style>
